// Renderer.ts
import type { BoardModel } from './BoardModel';
import { IUser } from './IUser';
import { PresenceMeta } from './LobbyManager';
import { Vec, len, clamp, add, mul, applyFriction, estimateSpin } from './PhysicsEngine';
import { WorldSpace } from './WorldSpace';
import { RendererTemplates } from './RendererTemplates';
import { ProfileManager, ProfileHandlers } from './ProfileManager';
import { BoardLighting } from './BoardLighting';
import { messageManager, GameState } from './messageManager';
import { LEDScoreBar, LEDScoreBarConfig } from './LEDScoreBar';
import './Renderer.css';
import './widgets/Widgets.css';
import { avatarDataUriForName } from './avatar';
import { WidgetManager } from './widgets/WidgetManager';
import { sidePanelConfig } from './widgets/widgetConfig';
import { TurnScore, deriveTurnScoreHistory } from './index';
import { TutorialManager } from './tutorial/TutorialManager';
import { showBoxscoreModal, reconstructGame } from './boxscore';
export type BallView = { pos:{x:number;y:number}; r:number };
// Cap devicePixelRatio to reduce overdraw on high-DPI 4K displays (Chrome on Windows)
const MAX_DPR = 1.5;


export type RendererHandlers = {
  resetGame?: () => void;
  requestRestart?: () => void;
  requestQuit?: () => void;
  returnToLobby?: () => void;
  getGameById?: (gameId: string) => Promise<any>;
  nextBall?: () => void;
  forgetUserLocal?: () => void;
  update?: (n:number) => void;
  getUserPreferences?: (userId: string) => Promise<any>;
  updateUserPreferences?: (userId: string, prefs: any) => Promise<any>;
  getUserData?: (userId: string) => Promise<any>;
  updateUserData?: (userId: string, userData: Partial<IUser>) => Promise<any>;
  uploadProfileImage?: (userId: string, file: File) => Promise<string | null>;
  listGamesByUser?: (userId: string) => Promise<any[]>;
  calculateUserPPR?: (userId: string) => Promise<number>;
  calculateWinLoss?: (userId: string, options?: { excludeTournaments?: boolean; excludeAI?: boolean }) => Promise<{ wins: number; losses: number; gamesPlayed: number }>;
  getUserTournamentParticipations?: (userId: string) => Promise<any[]>;
  getTournamentPlayersWithStats?: (tournamentId: string) => Promise<any[]>;
  saveTournamentAwardWinners?: (tournamentId: string, awards: any) => Promise<void>;
  onUsernameChanged?: (newUsername: string) => void;
  onCurrentUserUpdated?: (userData: Partial<IUser>) => void;
  showUserProfile?: (userId: string, userName: string) => Promise<void>;
  setRollDeadline?: () => void;
  // timer lifecycle
  startTimer?: () => void;   // start countdown for the current ball
  pauseTimer?: () => void;   // pause during modals
  resumeTimer?: () => void;  // resume when modal closes
  // Lobby actions
  lobbyInvite?: (user: PresenceMeta) => void;  // when Invite clicked with a selected user
  lobbySolo?: () => void;                      // when Solo Game is chosen
  lobbyFindMatch?: () => void;                 // when Find Match is clicked
  refreshLobby?: () => void;                   // refresh lobby data (polling)
  // End-game actions
  requestRematch?: () => void;                 // when Rematch button is clicked
  // Tournaments
  createTournament?: (args: { name: string; joinCode: string; maxPlayers?: number; type?: 'bracket' | 'round_robin' }) => Promise<{ id: string; name: string; join_code: string }>;
  goToTournamentLobby?: (tournamentId: string) => void;
  joinTournamentByCode?: (code: string) => Promise<{ tournamentId: string; name: string }>;
  listMyTournaments?: () => Promise<Array<{ id: string; name: string; status: string; bracket_data?: any; creator_id: string }>>;
  // Observer mode
  exitObserverMode?: () => void;
  // input is grouped so we don't clutter
  input?: {
    down?: (p: {x:number;y:number}, time: number) => void;
    move?: (p: {x:number;y:number}, time: number) => void;
    up?:   (p: {x:number;y:number}, time: number) => void;
  }
};

export class Renderer {
  currentUser: IUser | null;
  isMyTurn: boolean = false;
  players: number;
  canvas!: HTMLCanvasElement;
  overlay!: HTMLDivElement;
  ball: { pos:Vec; vel:Vec; r:number; spin:number; rolling:boolean };
  ballsLeft: number;
  baseBallR = 10; // starting ball size
  round: number = 1;
  raf = 0;
  world!: WorldSpace;
  private lastTime = 0;
  ctx!: CanvasRenderingContext2D;
  ballColor: string = '#9a9a9a'; // Default silver ball color
  boardBackgroundColor: string = '#5d432c'; // Default brown board background
  dpr = Math.min(MAX_DPR, Math.max(1, (window as any).devicePixelRatio || 1));
  private lobbyRoot!: HTMLDivElement;
  private lobbyListEl!: HTMLDivElement;
  private lobbyInviteBtn!: HTMLButtonElement;
  private lobbySoloBtn!: HTMLButtonElement;
  private lobbySelected: PresenceMeta | null = null;
  private boundResize?: (e: UIEvent) => void;
  private boundClickCloseMenu?: (e: MouseEvent) => void;
  public profileManager!: ProfileManager;
  private opponentHasQuit = false;
  private isTournamentGame = false;
  private tournamentPlayerRecords: Record<string, { wins: number; losses: number }> = {};
  private isIntroActive = false;
  public isAIGame: boolean = false;
  public isGameOver = false;
  private _isObserver = false;
  public gameState: GameState | null = null;
  public stats: IUser | null = null;
  private currentMatchId: string | null = null;
  private lastP1: IUser | null = null;
  private lastP2: IUser | null = null;
  private lastPhase: 'normal' | 'over' | 'retribution' | 'sudden_death' | undefined;
  private leftWidgetManager?: WidgetManager;
  private rightWidgetManager?: WidgetManager;
  // When true, we are in a special phase (retribution or sudden death) and
  // should not show any skeech/target hole lighting. This guards against
  // late passive path events re-lighting holes after a phase transition.
  private lightingFreezeUntil: number = 0;
  // Tutorial state
  private tutorialManager: TutorialManager = new TutorialManager();
  private tutorialBlocker: HTMLDivElement | null = null;
  private nextBallButton: HTMLButtonElement | null = null;

  public reset() {
    this.opponentHasQuit = false;
    this.isGameOver = false;
    this.isObserver = false;
    this.currentMatchId = null;
  }

  ovHTML: string = RendererTemplates.overlay;

  private handlers: RendererHandlers = {};
  setHandlers(h: RendererHandlers) {
    this.handlers = h;

    // Initialize ProfileManager with handlers
    this.profileManager = new ProfileManager({
      getUserPreferences: this.handlers.getUserPreferences,
      updateUserPreferences: this.handlers.updateUserPreferences,
      getUserData: this.handlers.getUserData,
      uploadProfileImage: this.handlers.uploadProfileImage,
      forgetUserLocal: this.handlers.forgetUserLocal,
      listGamesByUser: this.handlers.listGamesByUser,
      calculateUserPPR: this.handlers.calculateUserPPR,
      calculateWinLoss: this.handlers.calculateWinLoss,
      getUserTournamentParticipations: this.handlers.getUserTournamentParticipations,
      getTournamentPlayersWithStats: this.handlers.getTournamentPlayersWithStats,
      updateCurrentUser: async (userData: Partial<IUser>) => {
        if (this.currentUser && this.handlers.updateUserData) {
          await this.handlers.updateUserData(this.currentUser.id, userData);
          // Update local user object
          this.currentUser = { ...this.currentUser, ...userData };
          this.profileManager.setCurrentUser(this.currentUser);
          // Notify the main app that currentUser was updated
          this.handlers.onCurrentUserUpdated?.(userData);
          // Notify the main app that username changed (for backwards compatibility)
          if (userData.userName) {
            this.handlers.onUsernameChanged?.(userData.userName);
          }
          this.updateLobbyProfileImage();
        }
      }
    });

    // Set the current user in ProfileManager
    this.profileManager.setCurrentUser(this.currentUser);
  }

  setIsMyTurn(isMyTurn: boolean) {
    this.isMyTurn = isMyTurn;
    this.updateNextBallButtonState();
  }

  private canUseNextBall(): boolean {
    return !this.isObserver && this.isMyTurn && this.ball.rolling;
  }

  updateNextBallButtonState() {
    if (!this.nextBallButton) return;
    const disabled = !this.canUseNextBall();
    this.nextBallButton.disabled = disabled;
    this.nextBallButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');

    // Determine the appropriate tooltip message
    let title = 'Skip to the next ball';
    if (disabled) {
      if (!this.isMyTurn) {
        title = 'Wait for your turn before skipping ahead.';
      } else if (!this.ball.rolling) {
        title = 'Ball must be rolling to skip.';
      }
    }
    this.nextBallButton.title = title;
  }

  public get isObserver(): boolean {
    return this._isObserver;
  }

  public set isObserver(value: boolean) {
    if (this._isObserver === value) return;
    this._isObserver = value;
    this.updateNextBallButtonState();
  }

  public updateLobbyProfileImage() {
    const lobbyProfileImage = document.getElementById('lobbyProfileImage') as HTMLImageElement;
    if (lobbyProfileImage && this.currentUser) {
      lobbyProfileImage.src = this.currentUser.profile_image_url || avatarDataUriForName(this.currentUser.userName);
    }
  }

  setCurrentUser(user: IUser | null) {
    this.currentUser = user;
    if (this.profileManager) {
      this.profileManager.setCurrentUser(user);
    }
    this.updateLobbyProfileImage();
  }

  public setGameState(gameState: GameState) {
    this.gameState = gameState;
  }

  public setStats(stats: IUser) {
    this.stats = stats;
  }

  public setCurrentMatchId(matchId: string | null) {
    this.currentMatchId = matchId;
  }
  
  private floaters: Array<{x:number;y:number;pts:number;born:number;dur:number}> = [];
  private gradientCache: { [color: string]: string[] } = {};
  private boardLighting: BoardLighting = new BoardLighting();
  private player1LEDBar?: LEDScoreBar;
  private player2LEDBar?: LEDScoreBar;

  private generateBallGradient(baseColor: string): string[] {
    if (this.gradientCache[baseColor]) {
      return this.gradientCache[baseColor];
    }

    // Convert hex to RGB
    const hex = baseColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Helper function to create color stops
    const createColor = (rMod: number, gMod: number, bMod: number): string => {
      const newR = Math.max(0, Math.min(255, r + rMod));
      const newG = Math.max(0, Math.min(255, g + gMod));
      const newB = Math.max(0, Math.min(255, b + bMod));
      return `rgb(${newR}, ${newG}, ${newB})`;
    };

    // Generate 5-stop metallic gradient for 3D effect
    const gradient = [
      createColor(120, 120, 120), // Bright highlight (stop 0.0)
      createColor(60, 60, 60),    // Light reflection (stop 0.2)
      baseColor,                  // Base color (stop 0.55)
      createColor(-40, -40, -40), // Shadow (stop 0.85)
      createColor(-80, -80, -80)  // Deep shadow (stop 1.0)
    ];

    this.gradientCache[baseColor] = gradient;
    return gradient;
  }


  private addFloater(x:number, y:number, pts:number) {
    if (pts <= 0) return; // Don't show floaters for 0 or negative points

    this.floaters.push({ x, y, pts, born: performance.now(), dur: pts===5 ? 900 : 650 });

    
  }

  
  constructor(private board: BoardModel, world: WorldSpace, players: number, ballsLeft: number, user: IUser | null, ball: { pos:Vec; vel:Vec; r:number; spin:number; rolling:boolean }, round: number) {
    this.players = players;
    this.world = world;
    this.ballsLeft = ballsLeft;
    this.currentUser = user;
    this.ball = ball;
    this.round = round;
  }

  setTutorialManager(manager: TutorialManager) {
    this.tutorialManager = manager;
  }

  private ensureTutorialBlocker() {
    if (this.tutorialBlocker) return;
    this.tutorialBlocker = document.createElement('div');
    this.tutorialBlocker.className = 'tutorial-blocker-overlay';
    document.body.appendChild(this.tutorialBlocker);
  }

  setTutorialBlocking(blocked: boolean) {
    this.ensureTutorialBlocker();
    if (!this.tutorialBlocker) return;
    this.tutorialBlocker.classList.toggle('active', blocked);
    document.body.classList.toggle('tutorial-blocking', blocked);
  }

  resize() {
    this.world.setAspect(this.board.aspect);
    this.world.framePad       = this.board.framePad;
    this.world.moatPad        = this.board.moatPad;
    this.world.playPad        = this.board.playPad;
    this.world.slabHeightRatio= this.board.slabHeightRatio;
    this.board.resize(this.canvas, this.ctx, this.dpr);
    this.initializeLEDScoreBars();
  }

  private initializeLEDScoreBars() {
    if (!this.canvas) return;

    const { frame, moat, slab } = this.board.rects(this.canvas, this.dpr);

    // Score bar dimensions
    const barWidth = 18; // Wider to accommodate larger beads
    const barHeight = 330; // Taller for better bead spacing
    const beadSize = 8; // Larger beads like metro map
    const maxScore = 21; // Maximum possible score in skrollrollr

    // Position lower on the board (center + half bar height down)
    const boardCenterY = slab.y + slab.h / 2;
    const barY = boardCenterY;// + (barHeight / 4); // Bottom of bar at center, so bar extends down

    // Player 1 score bar (left side of board)
    const player1Config: LEDScoreBarConfig = {
      x: slab.x - 15, // Inside the left edge of the board
      y: barY,
      width: barWidth,
      height: barHeight,
      maxScore: maxScore,
      beadSize: beadSize
    };

    // Player 2 score bar (right side of board)
    const player2Config: LEDScoreBarConfig = {
      x: slab.x + slab.w - barWidth + 15, // Inside the right edge of the board
      y: barY,
      width: barWidth,
      height: barHeight,
      maxScore: maxScore,
      beadSize: beadSize
    };

    this.player1LEDBar = new LEDScoreBar(player1Config);
    this.player2LEDBar = new LEDScoreBar(player2Config);
  }

  private drawFloaters(ctx: CanvasRenderingContext2D, now:number) {
    for (let i = this.floaters.length - 1; i >= 0; i--) {
      const f = this.floaters[i];
      const t = (now - f.born) / f.dur;
      if (t >= 1) { this.floaters.splice(i,1); continue; }
      const alpha = 1 - t;
      const dy = -40 * t;
      const scale = f.pts===5 ? (1 + 0.15*Math.sin(t*8*Math.PI)) : 1;
      ctx.save();
      ctx.globalAlpha = alpha*0.9;
      ctx.fillStyle = "#fff";
      ctx.font = `bold ${Math.round(18*scale)}px system-ui,Segoe UI,Roboto`;
      ctx.textAlign = "center";
      ctx.fillText("+ "+f.pts, f.x, f.y + dy);
      ctx.restore();
    }
  }

  draw(ctx: CanvasRenderingContext2D, dpr: number, canvas: HTMLCanvasElement, ball: BallView) {
    const { frame, moat, play, slab } = this.board.rects(canvas, dpr);
    const W = frame.w, H = frame.h;

    ctx.clearRect(0, 0, W, H);

    const maple = ctx.createLinearGradient(0, 0, 0, H);
    maple.addColorStop(0, '#eadfcf'); maple.addColorStop(1, '#eadfcf');
    ctx.fillStyle = maple; ctx.fillRect(frame.x, frame.y, frame.w, frame.h);
    ctx.fillStyle = this.boardBackgroundColor; ctx.fillRect(moat.x, moat.y, moat.w, moat.h);

    // Draw perimeter chaser lights for pinball effect
    this.boardLighting.drawPerimeterChaser(ctx, moat);

    // Draw pinball messages (text with glow effects)
    this.boardLighting.drawMessages(ctx, moat);

    // Draw turn indicators ("YOUR" and "TURN" in corners)
    this.boardLighting.drawTurnIndicators(ctx, moat);

    // Draw balls left display in lower left corner
    this.boardLighting.drawBallsLeft(ctx, { x: moat.x, y: moat.y, width: moat.w, height: moat.h });

    const yg = this.board.holeGroups();
    const drawChevron = (ny: number, leftEdge: number, rightEdge: number, apexDepth: number) => {
      const y = slab.y + ny * slab.h;
      ctx.beginPath();
      ctx.moveTo(slab.x + slab.w * leftEdge, y);
      ctx.lineTo(slab.x + slab.w * 0.50,      y + slab.h * apexDepth);
      ctx.lineTo(slab.x + slab.w * rightEdge, y);
      ctx.stroke();
    };

    ctx.lineWidth = 4; ctx.strokeStyle = '#111'; ctx.fillStyle = '#111';
    const ch = this.board.chevrons;
    this.boardLighting.drawChevronWithLighting(ctx, slab, yg.y1 + ch.one.gap, ch.one.leftEdge, ch.one.rightEdge, ch.one.apexDepth, 1);
    this.boardLighting.drawChevronWithLighting(ctx, slab, yg.y3 + ch.three.gap, ch.three.leftEdge, ch.three.rightEdge, ch.three.apexDepth, 3);
    this.boardLighting.drawChevronWithLighting(ctx, slab, yg.y5 + ch.five.gap, ch.five.leftEdge, ch.five.rightEdge, ch.five.apexDepth, 5);

    ctx.font = 'bold 20px system-ui, -apple-system, Segoe UI, Roboto'; ctx.textAlign = 'center';
    ctx.fillText('1', slab.x + slab.w * 0.50, slab.y + yg.y1 * slab.h * 1.25);
    ctx.fillText('3', slab.x + slab.w * 0.50, slab.y + yg.y3 * slab.h * 1.22);
    ctx.fillText('5', slab.x + slab.w * 0.50, slab.y + yg.y5 * slab.h * 0.35);

    const holes = this.board.holesAbs(slab);
    for (const h of holes) {
      this.boardLighting.drawHoleWithLighting(ctx, h.cx, h.cy, h.r, h.pts);
    }

    ctx.strokeStyle = '#f2ede5'; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(play.x + play.w * 0.08, play.y + play.h - 18); ctx.lineTo(play.x + play.w * 0.92, play.y + play.h - 18); ctx.stroke();

    // Draw LED score bars (always show both) - before ball so ball appears on top
    if (this.player1LEDBar) {
      this.player1LEDBar.draw(ctx);
    }
    if (this.player2LEDBar) {
      this.player2LEDBar.draw(ctx);
    }

    // Always draw the game ball - rolling animation is separate visual element
    // The rolling animation should not prevent showing the actual game ball
    if (true) {
      const { play:playW } = this.world.worldRects();
      const ballScreen = this.world.worldToScreen(ball.pos, play, playW);
      const ballScreenR = this.world.worldLenToPx(ball.r, play, playW);
      const g = ctx.createRadialGradient(ballScreen.x - 0.35 * ballScreenR, ballScreen.y - 0.35 * + ballScreenR, Math.max(0.1, 0.1 * ballScreenR), ballScreen.x, ballScreen.y, ballScreenR);
      const gradientColors = this.generateBallGradient(this.ballColor);
      g.addColorStop(0.0, gradientColors[0]);
      g.addColorStop(0.2, gradientColors[1]);
      g.addColorStop(0.55, gradientColors[2]);
      g.addColorStop(0.85, gradientColors[3]);
      g.addColorStop(1.0, gradientColors[4]);
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.arc(ballScreen.x, ballScreen.y, ballScreenR, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(ballScreen.x - 0.35 * ballScreenR, ballScreen.y - 0.35 * ballScreenR, Math.max(1, ballScreenR * 0.38), 0, Math.PI * 2); ctx.fillStyle = 'rgba(255,255,255,0.25)'; ctx.fill();

      // Draw tutorial pointer if in tutorial mode
      this.tutorialManager.drawPointer(ctx, ballScreen.x, ballScreen.y, ballScreenR, performance.now());
    }

    this.drawFloaters(ctx, performance.now());
  }

  public notifyScored(cx:number, cy:number, pts:number) {
    this.addFloater(cx, cy, pts);
  }

  // Board lighting control methods
  public setHoleLighting(points: number, intensity: number = 0, effect: 'glow' | 'pulse' | 'rainbow' = 'glow', fill: boolean = false) {
    this.boardLighting.setHoleLighting(points, intensity, effect, fill);
  }

  public setZoneLighting(points: number, intensity: number = 0, effect: 'glow' | 'pulse' | 'rainbow' = 'glow') {
    this.boardLighting.setZoneLighting(points, intensity, effect);
  }

  public clearBoardLighting() {
    this.boardLighting.clearAll();
  }

  public startPinballLightShow() {
    this.boardLighting.startPinballShow();
  }

  public stopPinballLightShow() {
    this.boardLighting.stopPinballShow();
  }

  public isPinballShowActive(): boolean {
    return this.boardLighting.isShowActive();
  }

  /**
   * ðŸ’¬ SHOW PINBALL MESSAGE - Display glowing text on the board
   *
   * USAGE EXAMPLES:
   * - showPinballMessage("SLOW DOWN", 0.5, 0.3, { color: 'red', effect: 'pulse' })
   * - showPinballMessage("YOUR TURN", 0.5, 0.7, { color: 'amber', fontSize: 32 })
   * - showPinballMessage("NICE SHOT!", 0.5, 0.5, { color: 'green', effect: 'fade' })
   */
  public showPinballMessage(text: string, x: number, y: number, options?: {
    duration?: number;
    fontSize?: number;
    color?: string;
    effect?: 'glow' | 'pulse' | 'fade';
    intensity?: number;
  }) {
    this.boardLighting.showMessage(text, x, y, options);
  }

  public clearPinballMessages() {
    this.boardLighting.clearMessages();
  }

  /**
   * ðŸŒŠ TRIGGER SCORING BEAD RIPPLE EFFECT
   */
  public triggerScoringRipple() {
    if (this.player1LEDBar) {
      this.player1LEDBar.triggerRipple();
    }
    if (this.player2LEDBar) {
      this.player2LEDBar.triggerRipple();
    }
  }

  /**
   * ðŸŒŠ TRIGGER GAME START RIPPLE EFFECT - Animates all beads lighting up
   */
  public triggerGameStartRipple() {
    if (this.player1LEDBar) {
      this.player1LEDBar.triggerGameStartRipple();
    }
    if (this.player2LEDBar) {
      this.player2LEDBar.triggerGameStartRipple();
    }
  }

  /**
   * ðŸŽ¯ TURN INDICATOR CONTROL
   */
  public setTurnIndicator(active: boolean, playerName?: string, isCurrentUser?: boolean) {
    this.boardLighting.setTurnIndicator(active, playerName, isCurrentUser);
  }

  /**
   * ðŸ¥Š SHOW MATCH INTRODUCTION ON BOARD
   */
  public showMatchIntroOnBoard(player1Name: string, aiName: string, duration: number = 4000) {
    this.isIntroActive = true;
    this.boardLighting.showMatchIntro(player1Name, aiName, duration);
    setTimeout(() => {
        this.isIntroActive = false;
    }, duration);
  }

  public showTutorialIntroOnBoard(duration: number = 5000, onComplete?: () => void) {
    this.isIntroActive = true;
    this.boardLighting.showTutorialIntro(duration, onComplete);
    setTimeout(() => {
      this.isIntroActive = false;
    }, duration);
  }


  /**
   * âš”ï¸ SHOW RETRIBUTION MESSAGE ON BOARD
   */
  public showRetributionOnBoard(playerName: string, duration?: number) {
    this.boardLighting.showRetributionMessage(playerName, duration);
  }

  /**
   * âš¡ SHOW SUDDEN DEATH MESSAGE ON BOARD
   */
  public showSuddenDeathOnBoard(roundNumber: number, playerName: string, duration?: number) {
    this.boardLighting.showSuddenDeathMessage(roundNumber, playerName, duration);
  }

  /**
   * ðŸ’¥ SHOW BUST MESSAGE ON BOARD
   */
  public showBustOnBoard(playerName: string, bustTo: number, duration?: number) {
    this.boardLighting.showBustMessage(playerName, bustTo, duration);
  }

  /**
   * ðŸ† SHOW GAME OVER MESSAGE ON BOARD
   */
  public showGameOverOnBoard(isWinner: boolean, winnerName: string, loserName?: string, winnerScore?: number, loserScore?: number, isMultiplayer = false) {
    this.boardLighting.showGameOverMessage(isWinner, winnerName, loserName, winnerScore, loserScore, isMultiplayer);
  }

  /**
   * âš½ SET BALLS LEFT DISPLAY COUNT
   */
  public setBallsLeft(count: number) {
    this.boardLighting.setBallsLeft(count);
    // Also update the display color to match the current ball color
    this.boardLighting.setBallsLeftColor(this.ballColor);
  }

  public setBallsLeftVisible(visible: boolean) {
    this.boardLighting.setBallsLeftVisible(visible);
  }

  // ðŸš« LEGACY METHOD REMOVED - Use showPositionedMessage() instead

  /**
   * ðŸŽ¯ SHOW POSITIONED MESSAGE - Public access to positioned message system
   */
  public showPositionedMessage(text: string, messageData: any) {
    this.boardLighting.showPositionedMessage(text, messageData);
  }


  /**
   * ðŸŽ¯ START BALL ROLLING ANIMATION
   */
  public startBallRollingAnimation(targetScreenPos?: { x: number, y: number }) {
    const { frame, moat } = this.board.rects(this.canvas, this.dpr);
    this.boardLighting.startBallRollingAnimation(
      { x: moat.x, y: moat.y, width: moat.w, height: moat.h },
      this.ballColor,
      targetScreenPos
    );
  }

  public stopRollingAnimation() {
    this.boardLighting.stopRollingAnimation();
  }

  public isRollingAnimationActive(): boolean {
    return this.boardLighting.isRollingAnimationActive();
  }

  // Ensure the indicator hides one light to represent the ball at launch,
  // even if we don't show a pre-roll animation (e.g., some turn transitions).
  public setReserveAtLaunch(active: boolean) {
    (this.boardLighting as any).setReserveAtLaunch(active);
  }

  // LED Score Bar control methods
  public updateLEDScores(player1Score: number, player2Score?: number) {
    if (this.player1LEDBar) {
      this.player1LEDBar.setScore(player1Score);
    }
    if (this.player2LEDBar && player2Score !== undefined) {
      this.player2LEDBar.setScore(player2Score);
    }
  }


private updateBoardLighting(isSkeechPossible: boolean, turnHits: number[], wildcardHit: number | null) {
    // During retribution or sudden death, never light skeech/target holes.
    if (performance.now() < this.lightingFreezeUntil) {
      this.clearBoardLighting();
      return;
    }
    this.clearBoardLighting();

    if (!isSkeechPossible || turnHits.length === 0) {
        return;
    }

    const skeechHoles = [1, 3, 5];
    const uniqueSkeechHits = new Set(turnHits.filter(h => skeechHoles.includes(h)));
    const numUniqueSkeechHits = uniqueSkeechHits.size;

    if (numUniqueSkeechHits > 0) {
        const counts = new Map<number, number>();
        turnHits.forEach(hit => {
            if (skeechHoles.includes(hit)) {
                counts.set(hit, (counts.get(hit) || 0) + 1);
            }
        });

        uniqueSkeechHits.forEach(hitValue => {
            // Start with base brightness
            let brightness = 0;
            if (numUniqueSkeechHits === 1) brightness = 0.33;
            if (numUniqueSkeechHits === 2) brightness = 0.66;
            if (numUniqueSkeechHits === 3) brightness = 1.0;

            let shouldFill = false;
            const hitCount = counts.get(hitValue) || 0;

            // Condition 1: Double hit on first two rolls â€” triggers fill and full brightness
            if (turnHits.length <= 2 && hitCount >= 2) {
                shouldFill = true;
                brightness = 1.0;
            }
            // Condition 2: Third roll or later â€” triggers fill and full brightness for all hit holes
            if (turnHits.length >= 3) {
                shouldFill = true;
                brightness = 1.0;
            }

            this.setHoleLighting(hitValue, brightness, 'glow', shouldFill);
            this.setZoneLighting(hitValue, brightness * 0.75, 'glow');
        });
    }

    // --- Special Overlays for "One-Shot-Away" ---
    // This logic will correctly apply on top of the base lighting.
    if (turnHits.length === 3 && numUniqueSkeechHits === 2) {
        const neededHole = skeechHoles.find(h => !uniqueSkeechHits.has(h));
        if (neededHole) {
            // The hit holes are already set to fill and 1.0 brightness by the logic above.
            // We just need to add the rainbow effect to the "needed" hole.
            this.setHoleLighting(neededHole, 0.6, 'rainbow', false);
            this.setZoneLighting(neededHole, 0.4, 'rainbow');
        }
    }
    // If you have all 3 types, any of them will do for the 4th ball
    else if (numUniqueSkeechHits === 3) {
        uniqueSkeechHits.forEach(hitValue => {
            // This overrides the 'glow' effect with 'rainbow' and ensures fill is true.
            this.setHoleLighting(hitValue, 1.0, 'rainbow', true);
            this.setZoneLighting(hitValue, 1.0, 'rainbow');
        });
    }
  }


  buildDOM() {
    // CSS is now imported from Renderer.css
    // Legacy style injection removed

    // Overlay (unchanged)
    this.overlay = document.createElement("div");
    this.overlay.style.cssText = "position:fixed;inset:0;display:none;align-items:center;justify-content:center;background:rgba(0,0,0,0.6);z-index:5000";
    this.overlay.innerHTML = this.ovHTML;
    document.body.appendChild(this.overlay);
    //   this.handlers.resetGame?.();

    // --- Lobby Overlay (board is hidden when this is shown) ---
    this.lobbyRoot = document.createElement('div');
    this.lobbyRoot.id = 'skeech-lobby';
    this.lobbyRoot.innerHTML = RendererTemplates.lobby();
    document.body.appendChild(this.lobbyRoot);

    // --- Profile Modal is now handled by ProfileManager ---

    this.lobbyListEl = this.lobbyRoot.querySelector<HTMLDivElement>('#lobbyList')!;
    this.lobbyInviteBtn = this.lobbyRoot.querySelector<HTMLButtonElement>('#lobbyChallenge')!;
    this.lobbySoloBtn = this.lobbyRoot.querySelector<HTMLButtonElement>('#lobbySolo')!;

    // Find Match button
    const findMatchBtn = this.lobbyRoot.querySelector<HTMLButtonElement>('#lobbyFindMatch')!;
    // Tournament button
    const tournamentBtn = this.lobbyRoot.querySelector<HTMLButtonElement>('#lobbyTournament');

    this.lobbyRoot.querySelector<HTMLButtonElement>('#lobbyProfile')!.onclick = () => this.profileManager.showProfileModal();
    this.lobbySoloBtn.onclick = () => this.handlers.lobbySolo?.();

    findMatchBtn.onclick = () => {
      this.handlers.lobbyFindMatch?.();
    };

    if (tournamentBtn) {
      tournamentBtn.onclick = () => {
        this.showTournamentEntry();
      };
    }
    
  }

  private touchedThisBall = false;

  public resetTouchState() {
    this.touchedThisBall = false;
  }

  private createBottomBar(): HTMLDivElement {
    const bottomBar = document.createElement("div");
    bottomBar.id = "skeech-bottombar";

    // Center section (ball count with dots)
    const centerSection = document.createElement("div");
    centerSection.className = "topbar-section";

    const ballsLabel = document.createElement("span");
    ballsLabel.className = "topbar-info";
    ballsLabel.textContent = "Balls:";

    const ballsContainer = document.createElement("div");
    ballsContainer.className = "topbar-balls";
    ballsContainer.id = "bottombar-balls";
    this.updateBallDots(ballsContainer);

    centerSection.appendChild(ballsLabel);
    centerSection.appendChild(ballsContainer);

    // Right section (menu)
    const rightSection = document.createElement("div");
    rightSection.className = "topbar-section";
    rightSection.style.position = "relative";

    const menuBtn = document.createElement("button");
    menuBtn.className = "topbar-menu-btn";
    menuBtn.textContent = "Menu";
    menuBtn.onclick = () => this.toggleMenu('bottom-menu-dropdown');

    const menuDropdown = document.createElement("div");
    menuDropdown.className = "menu-dropdown";
    menuDropdown.id = "bottom-menu-dropdown";

    const menuItems: Array<{ label: string; handler: () => void | Promise<void> }> = [];
    if (this.isObserver) {
      // Observers can view scoring summary and exit
      menuItems.push({ label: "Show scoring summary", handler: async () => { this.hideMenu('bottom-menu-dropdown'); await this.showScoringSummary(); } });
      menuItems.push({ label: "Exit Observer Mode", handler: () => { this.hideMenu('bottom-menu-dropdown'); this.handlers.exitObserverMode?.(); } });
    } else {
      menuItems.push({ label: "Show scoring summary", handler: async () => { this.hideMenu('bottom-menu-dropdown'); await this.showScoringSummary(); } });
      menuItems.push({ label: "Back to Lobby", handler: () => { this.hideMenu('bottom-menu-dropdown'); this.handlers.returnToLobby?.(); } });
      menuItems.push({ label: "Restart", handler: () => { this.hideMenu('bottom-menu-dropdown'); this.handlers.requestRestart?.(); } });
      menuItems.push({ label: "Quit", handler: () => { this.hideMenu('bottom-menu-dropdown'); this.handlers.requestQuit?.(); } });
    }

    menuItems.forEach((item, index) => {
      const menuItem = document.createElement("button");
      menuItem.className = "menu-item";
      menuItem.textContent = item.label;
      menuItem.onclick = item.handler;
      menuDropdown.appendChild(menuItem);
    });

    rightSection.appendChild(menuBtn);
    rightSection.appendChild(menuDropdown);

    // Empty left section for spacing
    const leftSpacer = document.createElement("div");
    leftSpacer.className = "topbar-section";

    bottomBar.appendChild(leftSpacer);
    bottomBar.appendChild(centerSection);
    bottomBar.appendChild(rightSection);

    return bottomBar;
  }

  private toggleMenu(menuId: string, buttonElement?: HTMLElement) {
    const dropdown = document.getElementById(menuId);
    if (dropdown) {
        const isVisible = dropdown.classList.toggle("visible");

        if (isVisible && buttonElement) {
            const btnRect = buttonElement.getBoundingClientRect();
            dropdown.style.position = 'fixed';
            dropdown.style.right = `${window.innerWidth - btnRect.right}px`;
            dropdown.style.bottom = `${window.innerHeight - btnRect.top}px`;
        }
    }
  }

  private hideMenu(menuId: string) {
    const dropdown = document.getElementById(menuId);
    if (dropdown) {
      dropdown.classList.remove("visible");
    }
  }

  private async showScoringSummary() {
    const p1Name = this.lastP1?.userName || 'Player 1';
    const p2Name = this.lastP2?.userName || 'Player 2';
    let p1Rolls = this.lastP1?.current_game_rolls || [];
    let p2Rolls = this.lastP2?.current_game_rolls || [];
    let status: string | undefined;

    console.log('[Boxscore] Initial roll data from player objects:', {
      p1Rolls: [...p1Rolls],
      p2Rolls: [...p2Rolls],
      currentMatchId: this.currentMatchId,
      isObserver: this.isObserver
    });

    // Fetch freshest roll history if we know the match ID
    if (this.currentMatchId && this.handlers.getGameById) {
      try {
        const row = await this.handlers.getGameById(this.currentMatchId);
        if (row) {
          p1Rolls = Array.isArray(row.p1_roll_history) ? row.p1_roll_history : p1Rolls;
          p2Rolls = Array.isArray(row.p2_roll_history) ? row.p2_roll_history : p2Rolls;
          status = row.status;
          console.log('[Boxscore] Refreshed roll data from DB:', {
            matchId: this.currentMatchId,
            p1Rolls: [...p1Rolls],
            p2Rolls: [...p2Rolls],
            status,
            time_stamp: row.time_stamp,
            tournament_id: row.tournament_id
          });
        }
      } catch (e) {
        console.warn('[Boxscore] Failed to refresh roll history for match', this.currentMatchId, e);
      }
    }

    const isFinal = status === 'completed';
    const reconstructed = reconstructGame(p1Rolls, p2Rolls);
    const last = reconstructed.summaries[reconstructed.summaries.length - 1];
    let subtitle = `Round ${this.round}`;
    if (last?.phase === 'retribution') {
      subtitle = 'Retribution';
    } else if (last?.phase === 'sudden_death') {
      const sdRound = last.suddenDeathRound ? ` ${last.suddenDeathRound}` : '';
      subtitle = `Sudden Death${sdRound}`;
    }

    showBoxscoreModal({
      title: 'Scoring Summary',
      subtitle,
      p1Name,
      p2Name,
      p1Rolls,
      p2Rolls,
      showWinnerBadges: isFinal,
      contextNote: (!p1Rolls.length && !p2Rolls.length) ? 'No rolls recorded yet in this game.' : undefined
    });
  }

  private updateBallDots(container: HTMLElement) {
    container.innerHTML = "";
    for (let i = 0; i < this.ballsLeft; i++) {
      const dot = document.createElement("div");
      dot.className = "topbar-ball-dot";
      container.appendChild(dot);
    }
  }

  private createPlayerCard(title: string, playerIndex: number): HTMLDivElement {
    const card = document.createElement("div");
    card.className = "player-card";
    card.id = `player-card-${playerIndex}`;

    const imageElement = document.createElement("img");
    imageElement.className = "player-card-image";

    const cardTitle = document.createElement("div");
    cardTitle.className = "player-card-title";
    cardTitle.textContent = title;

    const cardName = document.createElement("div");
    cardName.className = "player-card-name";
    cardName.textContent = "Waiting...";

    const statsElement = document.createElement("div");
    statsElement.className = "player-card-stats";

    card.appendChild(imageElement);
    card.appendChild(cardTitle);
    card.appendChild(cardName);
    card.appendChild(statsElement);

    return card;
  }

  private updatePlayerCardInteractivity(cardElement: HTMLElement, playerName?: string, playerId?: string) {
    if (playerName && playerName !== "Waiting..." && playerId) {
      cardElement.style.cursor = 'pointer';
      cardElement.style.opacity = '1';
      cardElement.onclick = () => {
        console.log('Player card clicked:', playerName, playerId);
        this.profileManager.showUserProfile(playerId, playerName);
      };
      cardElement.title = `View ${playerName}'s profile`;
    } else {
      cardElement.style.cursor = 'default';
      cardElement.style.opacity = '0.7';
      cardElement.onclick = null;
      cardElement.title = '';
    }
  }

  private renderGameMenu() {
    const menuDropdown = document.getElementById("game-menu-dropdown");
    if (!menuDropdown) return;

    // Clear existing items
    menuDropdown.innerHTML = '';

    const menuItems: Array<{ label: string; handler: () => void | Promise<void> }> = [];
    const renderMenuItems = () => {
      menuItems.forEach((item) => {
        const menuItem = document.createElement("button");
        menuItem.className = "menu-item";
        menuItem.textContent = item.label;
        menuItem.onclick = item.handler;
        menuDropdown.appendChild(menuItem);
      });
    };

    if (this.tutorialManager.isActive) {
      menuItems.push({
        label: "Exit Tutorial",
        handler: () => {
          this.hideMenu('game-menu-dropdown');
          this.handlers.returnToLobby?.();
        }
      });
      renderMenuItems();
      return;
    }

    // Observer mode has special menu
    if (this.isObserver) {
      menuItems.push({ label: "Show scoring summary", handler: async () => { this.hideMenu('game-menu-dropdown'); await this.showScoringSummary(); } });
      menuItems.push({ label: "Exit Observer Mode", handler: () => { this.hideMenu('game-menu-dropdown'); this.handlers.exitObserverMode?.(); } });
      // Render and return early - observers get scoring summary and exit options
      renderMenuItems();
      return;
    }

    // Always provide a way back
    const backLabel = this.isTournamentGame ? "Back to Tournament" : "Back to Lobby";
    if (!this.isGameOver) {
      menuItems.push({ label: "Show scoring summary", handler: async () => { this.hideMenu('game-menu-dropdown'); await this.showScoringSummary(); } });
    }
    menuItems.push({ label: backLabel, handler: () => { this.hideMenu('game-menu-dropdown'); this.handlers.returnToLobby?.(); } });

    if (this.isTournamentGame) {
      // Tournament-specific menu
      if (!this.isGameOver) {
        // During game: allow Quit
        menuItems.push({ label: "Quit", handler: () => { this.hideMenu('game-menu-dropdown'); this.handlers.requestQuit?.(); } });
      }
      // After game: only Back to Lobby (already added). No Rematch/Restart in tournaments.
    } else {
      if (this.isGameOver) {
        // After non-tournament game ends
        if (this.isAIGame) {
          // AI games: just restart immediately
          menuItems.push({ label: "Restart", handler: () => { this.hideMenu('game-menu-dropdown'); this.handlers.requestRestart?.(); } });
        } else {
          // Multiplayer games: request rematch
          menuItems.push({ label: "Rematch", handler: () => { this.hideMenu('game-menu-dropdown'); this.handlers.requestRematch?.(); } });
        }
      } else {
        // During non-tournament games, show Restart and Quit
        menuItems.push({ label: "Restart", handler: () => { this.hideMenu('game-menu-dropdown'); this.handlers.requestRestart?.(); } });
        if (!this.isAIGame) {
          menuItems.push({ label: "Quit", handler: () => { this.hideMenu('game-menu-dropdown'); this.handlers.requestQuit?.(); } });
        }
      }
    }

    renderMenuItems();
  }

  // Public wrapper to refresh game menu items when mode changes (e.g., observer)
  public refreshMenu() { this.renderGameMenu(); }

  public updatePlayerCards(p1?: IUser | null, p2?: IUser | null) {
    const leftCard = document.getElementById("player-card-0");
    const rightCard = document.getElementById("player-card-1");

    if (leftCard) {
        const nameEl = leftCard.querySelector('.player-card-name') as HTMLElement;
        const statsEl = leftCard.querySelector('.player-card-stats') as HTMLElement;
        const imageEl = leftCard.querySelector('.player-card-image') as HTMLImageElement;

        if (p1) {
            if (nameEl) {
                nameEl.textContent = p1.userName;
            }
            if (statsEl) {
                const ppr = p1.avgPPR || 0;
                // Check if we have tournament-specific W-L records (round-robin tournaments)
                const tournamentRecord = p1.id ? this.tournamentPlayerRecords[p1.id] : undefined;
                const wins = tournamentRecord ? tournamentRecord.wins : (p1.wins || 0);
                const losses = tournamentRecord ? tournamentRecord.losses : (p1.losses || 0);

                // Show W-L for round-robin tournaments (which have tournament records)
                // Show W-L for non-tournament games
                // Hide W-L for bracket tournaments (no tournament records)
                // Hide W-L for AI challenge mode
                const isAIChallenge = p2?.id === 'ai_robo_roller';
                if ((this.isTournamentGame && !tournamentRecord) || isAIChallenge) {
                    // Bracket tournament or AI challenge - hide W-L
                    statsEl.innerHTML = `
                        <div class="stat">
                            <span class="stat-label">PPR</span>
                            <span class="stat-value">${ppr.toFixed(2)}</span>
                        </div>
                    `;
                } else {
                    // Non-tournament game or round-robin tournament - show W-L + PPR
                    statsEl.innerHTML = `
                        <div class="stat">
                            <span class="stat-label">W-L</span>
                            <span class="stat-value">${wins}-${losses}</span>
                        </div>
                        <div class="stat">
                            <span class="stat-label">PPR</span>
                            <span class="stat-value">${ppr.toFixed(2)}</span>
                        </div>
                    `;
                }
            }
            if (imageEl) {
                imageEl.src = p1.profile_image_url || avatarDataUriForName(p1.userName);
            }
        } else {
            if (nameEl) nameEl.textContent = "Waiting...";
            if (statsEl) statsEl.innerHTML = "";
            if (imageEl) imageEl.src = avatarDataUriForName('waiting');
        }
        this.updatePlayerCardInteractivity(leftCard, p1?.userName, p1?.id);
    }

    if (rightCard) {
        const nameEl = rightCard.querySelector('.player-card-name') as HTMLElement;
        const statsEl = rightCard.querySelector('.player-card-stats') as HTMLElement;
        const imageEl = rightCard.querySelector('.player-card-image') as HTMLImageElement;

        if (p2) {
            if (nameEl) {
                nameEl.textContent = p2.userName;
            }
            if (statsEl) {
                const ppr = p2.avgPPR || 0;
                // Check if we have tournament-specific W-L records (round-robin tournaments)
                const tournamentRecord = p2.id ? this.tournamentPlayerRecords[p2.id] : undefined;
                const wins = tournamentRecord ? tournamentRecord.wins : (p2.wins || 0);
                const losses = tournamentRecord ? tournamentRecord.losses : (p2.losses || 0);

                // Show W-L for round-robin tournaments (which have tournament records)
                // Show W-L for non-tournament games
                // Hide W-L for bracket tournaments (no tournament records)
                // Hide W-L for AI challenge mode
                const isAIChallenge = p2.id === 'ai_robo_roller';
                if ((this.isTournamentGame && !tournamentRecord) || isAIChallenge) {
                    // Bracket tournament or AI challenge - hide W-L
                    statsEl.innerHTML = `
                        <div class="stat">
                            <span class="stat-label">PPR</span>
                            <span class="stat-value">${ppr.toFixed(2)}</span>
                        </div>
                    `;
                } else {
                    // Non-tournament game or round-robin tournament - show W-L + PPR
                    statsEl.innerHTML = `
                        <div class="stat">
                            <span class="stat-label">W-L</span>
                            <span class="stat-value">${wins}-${losses}</span>
                        </div>
                        <div class="stat">
                            <span class="stat-label">PPR</span>
                            <span class="stat-value">${ppr.toFixed(2)}</span>
                        </div>
                    `;
                }
            }
            if (imageEl) {
                imageEl.src = p2.profile_image_url || avatarDataUriForName(p2.userName);
            }
        } else {
            if (nameEl) nameEl.textContent = "Waiting...";
            if (statsEl) statsEl.innerHTML = "";
            if (imageEl) imageEl.src = avatarDataUriForName('waiting');
        }
        this.updatePlayerCardInteractivity(rightCard, p2?.userName, p2?.id);
    }
  }

  public updateGameStatus(status: string) {
    const statusIndicator = document.getElementById("game-status-indicator");
    if (statusIndicator) {
      statusIndicator.textContent = status;
    }
  }

  

  private createAbacusSection(playerId: string): HTMLDivElement {
    const abacusContainer = document.createElement("div");
    abacusContainer.className = "abacus-container";
    abacusContainer.id = `abacus-${playerId.toLowerCase().replace(/\s/g, '')}`;
    
    const abacusTitle = document.createElement("div");
    abacusTitle.className = "abacus-title";
    abacusTitle.textContent = "Score";
    
    const abacusScore = document.createElement("div");
    abacusScore.className = "abacus-score";
    abacusScore.textContent = "0";
    
    abacusContainer.appendChild(abacusTitle);
    abacusContainer.appendChild(abacusScore);
    
    return abacusContainer;
  }

  private buildBoard() {
    // Stop any previous animation loop and listeners before rebuilding to avoid duplicate RAF and flicker
    if (this.raf) { cancelAnimationFrame(this.raf); this.raf = 0; }
    if (this.boundResize) { try { window.removeEventListener("resize", this.boundResize); } catch {} this.boundResize = undefined; }
    this.leftWidgetManager?.cleanup();
    this.rightWidgetManager?.cleanup();

    const root = document.createElement("div");
    root.id = "skeech-root";

    // Left panel (player 1)
    const leftPanel = document.createElement("div");
    leftPanel.id = "skeech-left-panel";

    // Center (board)
    const centerCol = document.createElement("div");
    centerCol.id = "skeech-center";

    // Right panel (player 2)
    const rightPanel = document.createElement("div");
    rightPanel.id = "skeech-right-panel";

    // --- Board frame: absolutely no extra chrome; canvas sits inside ---
    const frame = document.createElement("div");
    frame.id = "skeech-frame";
    frame.style.position = "relative"; // Allow absolute positioning for overlay
    this.canvas = document.createElement("canvas");
    this.canvas.style.touchAction = "none";
    this.canvas.style.display = "block";
    frame.appendChild(this.canvas);

    // Add RETRIBUTION overlay
    const retributionOverlay = document.createElement("div");
    retributionOverlay.className = "retribution-overlay";
    retributionOverlay.id = "retribution-overlay";
    const retributionText = document.createElement("div");
    retributionText.className = "retribution-text";
    retributionText.textContent = "🔪";
    retributionOverlay.appendChild(retributionText);
    retributionOverlay.appendChild(retributionText);
    retributionOverlay.appendChild(retributionText);
    frame.appendChild(retributionOverlay);

    // Add SUDDEN DEATH overlay
    const suddenDeathOverlay = document.createElement("div");
    suddenDeathOverlay.className = "sudden-death-overlay";
    suddenDeathOverlay.id = "sudden-death-overlay";
    const suddenDeathSkull = document.createElement("div");
    suddenDeathSkull.className = "sudden-death-skull";
    suddenDeathSkull.textContent = "💀";
    suddenDeathOverlay.appendChild(suddenDeathSkull);
    frame.appendChild(suddenDeathOverlay);

    // Add OBSERVER MODE badge
    const observerBadge = document.createElement("div");
    observerBadge.className = "observer-mode-badge";
    observerBadge.id = "observer-mode-badge";
    const observerIcon = document.createElement("span");
    observerIcon.className = "observer-icon";
    observerIcon.textContent = "👁️";
    const observerText = document.createElement("span");
    observerText.className = "observer-text";
    observerText.textContent = "OBSERVER";
    observerBadge.appendChild(observerIcon);
    observerBadge.appendChild(observerText);
    frame.appendChild(observerBadge);

    centerCol.appendChild(frame);

    // --- Player cards for side panels ---
    const leftPlayerCard = this.createPlayerCard("", 0);
    leftPanel.appendChild(leftPlayerCard);

    const leftWidgetArea = document.createElement("div");
    leftWidgetArea.style.minHeight = "0";
    leftPanel.appendChild(leftWidgetArea);
    
    // const leftAbacus = this.createAbacusSection("Player 1");
    // leftPanel.appendChild(leftAbacus);

    // const leftSkeechTracker = this.createSkeechTracker(0);
    // leftPanel.appendChild(leftSkeechTracker);

    // Next Ball button
    const nextBallButton = document.createElement("button") as HTMLButtonElement;
    nextBallButton.textContent = "⏭️";
    nextBallButton.className = "skeech-btn next-ball-btn bottom-gutter-btn";
    nextBallButton.onclick = () => {
      if (!this.canUseNextBall()) {
        return;
      }
      this.handlers.nextBall?.();
    };
    this.nextBallButton = nextBallButton;
    this.updateNextBallButtonState();
    leftPanel.appendChild(nextBallButton);
    // Hide Next Ball button for observers
    if (this.isObserver) {
      try { (nextBallButton as HTMLButtonElement).disabled = true; } catch {}
      (nextBallButton as HTMLButtonElement).style.display = 'none';
      nextBallButton.onclick = null as any;
    }
    
    const rightPlayerCard = this.createPlayerCard("", 1);
    rightPanel.appendChild(rightPlayerCard);

    const rightWidgetArea = document.createElement("div");
    rightWidgetArea.style.minHeight = "0";
    rightPanel.appendChild(rightWidgetArea);

    // Menu button (offset color from Next Ball button) - positioned at bottom right
    const menuButton = document.createElement("button");
    menuButton.textContent = "☰";
    menuButton.className = "skeech-btn menu-btn bottom-gutter-btn";
    menuButton.style.marginTop = "auto"; // Push to bottom like Next Ball button
    menuButton.onclick = () => {
      this.renderGameMenu();
      this.toggleMenu('game-menu-dropdown', menuButton);
    };
    rightPanel.appendChild(menuButton);

    // Menu dropdown for the new menu button
    const menuDropdown = document.createElement("div");
    menuDropdown.className = "menu-dropdown";
    menuDropdown.id = "game-menu-dropdown";

    rightPanel.appendChild(menuDropdown);
    
    // const rightAbacus = this.createAbacusSection("Player 2");
    // rightPanel.appendChild(rightAbacus);

    // const rightSkeechTracker = this.createSkeechTracker(1);
    // rightPanel.appendChild(rightSkeechTracker);

    // Mount panels (bottom bar removed)
    root.appendChild(leftPanel);
    root.appendChild(centerCol);
    root.appendChild(rightPanel);

    // Remove only the existing game root if it exists, preserve other elements like profile modal
    const existingRoot = document.getElementById('skeech-root');
    if (existingRoot) {
      existingRoot.remove();
    }
    document.body.appendChild(root);

    this.leftWidgetManager = new WidgetManager(sidePanelConfig.leftPanel, leftWidgetArea);
    this.rightWidgetManager = new WidgetManager(sidePanelConfig.rightPanel, rightWidgetArea);

    // Profile modal is now handled by ProfileManager
    // Context + loop
    const ctx = (this.canvas.getContext("2d", { alpha: false } as any) || this.canvas.getContext("2d")) as CanvasRenderingContext2D | null;
    if (!ctx) throw new Error("Canvas 2D not supported or context unavailable");
    this.ctx = ctx;

    this.resize();
    this.boundResize = () => this.resize();
    window.addEventListener("resize", this.boundResize);
    this.canvas.addEventListener("pointerdown", e => this.handleDown(e));
    this.canvas.addEventListener("pointermove", e => this.handleMove(e));
    this.canvas.addEventListener("pointerup", e => this.handleUp(e));
    this.canvas.addEventListener("pointercancel", e => this.handleUp(e));

    this.lastTime = 0;
    const step = (now: number) => { 
      if (this.lastTime === 0) {
        this.lastTime = now;
        this.raf = requestAnimationFrame(step);
        return;
      }

      const dt = (now - this.lastTime) / 1000;
      this.lastTime = now;

      // Cap dt to prevent physics glitches on long frames
      const maxDt = 1 / 30;
      this.handlers.update?.(Math.min(dt, maxDt));

      if (this.stats && this.gameState && this.currentUser) {
        // Message logic moved to handleUp
      }

      this.draw(this.ctx, this.dpr, this.canvas, this.ball);
      this.raf = requestAnimationFrame(step); 
    };
    this.raf = requestAnimationFrame(step);

    // Close game menu when clicking outside
    this.boundClickCloseMenu = (e: MouseEvent) => {
      const dropdown = document.getElementById('game-menu-dropdown');
      const menuBtn = e.target as HTMLElement;
      if (dropdown && dropdown.classList.contains('visible') && !dropdown.contains(menuBtn) && !menuBtn.classList.contains('menu-btn')) {
        this.hideMenu('game-menu-dropdown');
      }
    };
    document.addEventListener('click', this.boundClickCloseMenu);

    //this.selfTest();
  }

  public stopLoop() {
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
  }

  public dispose() {
    // Stop RAF loop
    this.stopLoop();
    // Remove listeners
    if (this.boundResize) { window.removeEventListener('resize', this.boundResize); this.boundResize = undefined; }
    if (this.boundClickCloseMenu) { document.removeEventListener('click', this.boundClickCloseMenu); this.boundClickCloseMenu = undefined; }
    if (this.canvas) {
      this.canvas.removeEventListener('pointerdown', (e) => this.handleDown(e));
      this.canvas.removeEventListener('pointermove', (e) => this.handleMove(e));
      this.canvas.removeEventListener('pointerup', (e) => this.handleUp(e));
      this.canvas.removeEventListener('pointercancel', (e) => this.handleUp(e));
    }
    // Clear transient visuals
    this.clearPinballMessages();
    this.boardLighting.stopPinballShow();
    this.boardLighting.stopRollingAnimation();
  }

  private handleDown(e: PointerEvent) {
    if (this.tutorialManager.shouldBlockInteractions()) return;
    if (!this.isMyTurn) return;

    // Stop pinball light show when player starts rolling
    if (this.isPinballShowActive()) {
      this.stopPinballLightShow();
    }

    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const p = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    if (!this.touchedThisBall) {
      this.touchedThisBall = true;
      this.handlers.startTimer?.();  // start countdown now
    }
    this.handlers.input?.down?.(p, performance.now());
  }
  private handleMove(e: PointerEvent) {
    if (this.tutorialManager.shouldBlockInteractions()) return;
    if (!this.isMyTurn) return;
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const p = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    this.handlers.input?.move?.(p, performance.now());
  }
  private handleUp(e: PointerEvent) {
    if (this.tutorialManager.shouldBlockInteractions()) return;
    if (!this.isMyTurn) return;
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const p = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    this.handlers.input?.up?.(p, performance.now());

    // ðŸš« LEGACY MESSAGE SYSTEM REMOVED - Now using unified message manager in index.tsx
    // All messages are handled through getMessageWithPriority() in the main game logic
  }

  // Runtime self-tests
  selfTest() {
    try {
      console.assert(this.canvas instanceof HTMLCanvasElement, 'Canvas element exists');
      console.assert(!!this.ctx, '2D context is available');
      
      console.assert(document.body.contains(this.canvas), 'Canvas attached');
      console.assert(document.body.contains(this.overlay), 'Overlay attached');
    } catch (e) { console.error('Self-test failed', e); throw e; }
  }

  public resetTrackers() {
    for (let i = 0; i < this.players; i++) {
        const trackerCard = document.getElementById(`skeech-tracker-card-${i}`);
        if (!trackerCard) continue;

        trackerCard.classList.remove('visible', 'hiding', 'success');
        trackerCard.classList.add('hidden');

        // Reset lights and wildcard border
        trackerCard.querySelectorAll('.lit').forEach(el => el.classList.remove('lit'));
        trackerCard.querySelectorAll('.wildcard').forEach(el => el.classList.remove('wildcard'));
    }
  }

  public updateSpeedWidgets(speed: number) {
    this.leftWidgetManager?.updateSpeed(speed);
    this.rightWidgetManager?.updateSpeed(speed);
  }

  updateHUD(currentPlayer: number, scores: number[], turnHits: number[], isSkeechPossible: boolean, isSkeechSuccess: boolean, wildcardHit: number | null, p1?: IUser | null, p2?: IUser | null, playerCount?: number, phase?: 'normal' | 'over' | 'retribution' | 'sudden_death', bustHistory?: Array<{ player: 0 | 1; round: number }>) {
    // Update board lighting based on skeech status
    this.updateBoardLighting(isSkeechPossible, turnHits, wildcardHit);

    // Update LED score bars
    if (scores.length > 0) {
      this.updateLEDScores(scores[0], scores[1]);
    }

    // Update top bar round info
    const roundInfo = document.getElementById("topbar-round");
    if (roundInfo) {
      roundInfo.textContent = `Round ${this.round}`;
    }

    // Bottom bar balls display removed

    // Update player cards and apply active turn indicator
    this.updatePlayerCards(p1, p2);
    const leftCard = document.getElementById("player-card-0");
    const rightCard = document.getElementById("player-card-1");

    if (leftCard) {
        leftCard.classList.toggle('active-turn', currentPlayer === 0);
    }

    if (rightCard) {
        rightCard.classList.toggle('active-turn', currentPlayer === 1);
    }



    // --- Update Widgets ---
    if (this.currentUser) {
        // Derive turn score history from roll history
        const p1Rolls = p1?.current_game_rolls || [];
        const p2Rolls = p2?.current_game_rolls || [];

        // Cache last seen players/phase for live boxscore modal
        this.lastP1 = p1 || null;
        this.lastP2 = p2 || null;
        this.lastPhase = phase;

        const turnScoreHistory = deriveTurnScoreHistory(p1Rolls, p2Rolls);

        const widgetGameState = {
            round: this.round,
            roll: 3 - this.ballsLeft, // Assuming 3 balls per turn
            turnHits: turnHits,
            scores: scores,
            currentPlayerId: this.currentUser.id,
            currentPlayer: currentPlayer,
            testMode: (this as any).testMode || false, // Pass test mode flag to widgets
            turnScoreHistory: turnScoreHistory,
            player1Name: p1?.userName || 'Player 1',
            player2Name: p2?.userName || 'Player 2',
            phase: phase || 'normal',
            bustHistory: bustHistory || [],
        };
        this.leftWidgetManager?.updateState(widgetGameState);
        this.rightWidgetManager?.updateState(widgetGameState);

        const speed = this.ball.rolling ? len(this.ball.vel) : 0;
        this.leftWidgetManager?.updateSpeed(speed);
        this.rightWidgetManager?.updateSpeed(speed);
    }

    // Update abacus scores
    const player1Abacus = document.getElementById("abacus-player1");
    const player2Abacus = document.getElementById("abacus-player2");

    // Debug: Check what abacus elements actually exist
    const allElements = document.querySelectorAll('[id*="abacus"]');
    
    if (player1Abacus && scores.length > 0) {
      const scoreEl = player1Abacus.querySelector(".abacus-score");
      console.log('Player1 abacus scoreEl found:', !!scoreEl);
      if (scoreEl) {
        const scoreValue = scores[0]?.toString() || "0";
        console.log('Setting player1 score to:', scoreValue, 'from scores[0]:', scores[0]);
        scoreEl.textContent = scoreValue;
      }
    }

    if (player2Abacus && scores.length > 1) {
      const scoreEl = player2Abacus.querySelector(".abacus-score");
      console.log('Player2 abacus scoreEl found:', !!scoreEl);
      if (scoreEl) {
        const scoreValue = scores[1]?.toString() || "0";
        console.log('Setting player2 score to:', scoreValue, 'from scores[1]:', scores[1]);
        scoreEl.textContent = scoreValue;
      }
    }
  }

  private modalOpen = false;

  public showModal(opts: { title:string; body:string; button?:string; buttonIcon?: string; onClose?:()=>void }) {
    const { title, body, onClose, buttonIcon } = opts;
    
    
    // Ensure overlay exists and is attached to DOM
    if (!this.overlay || !document.body.contains(this.overlay)) {
      this.overlay = document.createElement("div");
      this.overlay.style.cssText = "position:fixed;inset:0;display:none;align-items:center;justify-content:center;background:rgba(0,0,0,0.8);z-index:9999";
      document.body.appendChild(this.overlay);
    }
    
    const html = RendererTemplates.modal(title, body);
    
    this.overlay.innerHTML = html;
    this.overlay.style.display = "flex";
    this.modalOpen = true;
    this.handlers.pauseTimer?.();
    
    
    const btn = this.overlay.querySelector<HTMLButtonElement>("#modalBtn");
    const iconEl = this.overlay.querySelector<HTMLDivElement>(".icon");
    if (iconEl && buttonIcon) {
      iconEl.textContent = buttonIcon;
    }
    if (btn) {
      btn.onclick = () => {
        this.overlay.style.display = "none";
        this.modalOpen = false;
        this.handlers.resumeTimer?.();
        onClose?.();
      };
    } else {
      console.error("[showModal] Button not found in modal!");
    }
  }

  public showTimeoutLostRoll() {
    this.showModal({
      title: "Timeâ€™s up",
      body: "You ran out of time. That roll counts as 0.",
      button: "Got it"
    });
  }

  public updateModalBody(newBody: string) {
    if (!this.modalOpen) return;
    const bodyEl = this.overlay.querySelector<HTMLDivElement>('#modalBody');
    if (bodyEl) {
      bodyEl.innerHTML = newBody;
    }
  }

  public endGame(winner: IUser | { userId: string; userName: string }, loser: IUser | { userId: string; userName: string } | null, winnerScore: number, loserScore: number, currentUser?: IUser) {
    this.isGameOver = true;
    if (this.opponentHasQuit) {
      this.opponentHasQuit = false; // Reset for next game
      return;
    }

    // Use board-based messaging instead of modal overlay
    const winnerId = 'userId' in winner ? winner.userId : winner.id;
    const isWinner = currentUser?.id === winnerId;
    const isMultiplayer = loser !== undefined && loser !== null;

    // Show game over message on board
    this.showGameOverOnBoard(
      isWinner,
      winner.userName,
      loser?.userName,
      winnerScore,
      loserScore,
      isMultiplayer
    );
  }

  private ensureWinOverlay(winner: IUser | { userId: string; userName: string }, loser?: IUser | { userId: string; userName: string } | null, scores?: [number, number], currentUser?: IUser) {
    const winnerId = 'userId' in winner ? winner.userId : winner.id;
    const isWinner = currentUser?.id === winnerId;
    const isMultiplayer = loser !== undefined && loser !== null;

    const winnerScore = 21;
    const loserScore = isMultiplayer && scores ? (scores[0] === 21 ? scores[1] : scores[0]) : 0;

    const title = isWinner ? 'Victory!' : 'Game Over';
    const subtitle = isWinner
      ? (isMultiplayer ? `You beat ${loser?.userName}!` : 'You hit 21!')
      : `${winner.userName} wins!`;

    const scoreDisplay = isMultiplayer
      ? `${winner.userName}: ${winnerScore} - ${loser!.userName}: ${loserScore}`
      : '';

    this.overlay.innerHTML = RendererTemplates.winOverlay(title, subtitle, scoreDisplay, isMultiplayer, isWinner);

    this.setupEndGameButtons(isMultiplayer);
  }

  private setupEndGameButtons(isMultiplayer: boolean) {
    const playAgainBtn = this.overlay.querySelector<HTMLButtonElement>('#playAgainBtn')!;
    playAgainBtn.onclick = () => {
      this.overlay.style.display = 'none';
      this.handlers.resetGame?.();
    };

    if (isMultiplayer) {
      playAgainBtn.style.display = 'none';
      const rematchBtn = this.overlay.querySelector<HTMLButtonElement>('#rematchBtn');
      const lobbyBtn = this.overlay.querySelector<HTMLButtonElement>('#lobbyBtn');

      // In tournament games, hide the Rematch option
      if (rematchBtn) {
        if (this.isTournamentGame) {
          rematchBtn.style.display = 'none';
        } else {
          rematchBtn.onclick = () => {
            this.overlay.style.display = 'none';
            this.handlers.requestRematch?.();
          };
        }
      }

      if (lobbyBtn) {
        // If this was a tournament game, clarify destination in the button label
        if (this.isTournamentGame) {
          const label = lobbyBtn.querySelector('.text') as HTMLDivElement | null;
          if (label) label.textContent = 'Return to Tournament';
        }
        lobbyBtn.onclick = () => {
          this.overlay.style.display = 'none';
          this.handlers.returnToLobby?.();
        };
      }
    }
  }

  showOverlay(msg?: string, buttonText?: string, overlayButtonHandler?: ()=>void) {
    if (!document.body.contains(this.overlay)) document.body.appendChild(this.overlay);

    // If we're trying to show a custom overlay but the current HTML doesn't have the expected structure,
    // rebuild it with the standard overlay structure
    if (msg || buttonText) {
      const existingBtn = this.overlay.querySelector<HTMLButtonElement>('#ovBtn');
      if (!existingBtn) {
        // Rebuild the overlay with the standard structure
        this.overlay.innerHTML = `
          <div style="background: rgba(0, 8, 48, 0.8); padding:20px 24px;border:1px solid #444;border-radius:16px;box-shadow:0 10px 40px rgba(0,0,0,.6);max-width:90vw;text-align:center">
            <div id="ovText" style="font-weight:700;font-size:22px;margin-bottom:8px;color:#fff"></div>
            <div id="ovSub" style="opacity:.85;margin-bottom:14px"></div>
            <button id="ovBtn" class="skeech-btn" style="width:auto">OK</button>
          </div>`;
      }
    }

    if(msg) {
      const txt = this.overlay.querySelector<HTMLDivElement>('#ovText')!;
      txt.textContent = msg;
    }
    if(buttonText) {
      const btn = this.overlay.querySelector<HTMLButtonElement>('#ovBtn')!;
      btn.textContent = buttonText;
      if(overlayButtonHandler) {
        btn.onclick = overlayButtonHandler;
      }
    }

    this.overlay.style.display = 'flex';
  }

  hideOverlay() {
    if (this.overlay) {
        this.overlay.style.display = 'none';
    }
  }

  showTwoButtonDialog(msg: string, button1Text: string, button1Handler: () => void, button2Text: string, button2Handler: () => void) {
    this.overlay.innerHTML = RendererTemplates.twoButtonDialog(msg, button1Text, button2Text);

    if (!document.body.contains(this.overlay)) document.body.appendChild(this.overlay);

    const btn1 = this.overlay.querySelector<HTMLButtonElement>('#btn1')!;
    const btn2 = this.overlay.querySelector<HTMLButtonElement>('#btn2')!;

    btn1.onclick = button1Handler;
    btn2.onclick = button2Handler;

    this.overlay.style.display = 'flex';
  }

  /** Show lobby and hide the board grid */
  public showLobby(users: PresenceMeta[]) {
    if (this.tutorialManager.isActive) {
      return;
    }
    // Defensive guard: Ensure lobby and board are mutually exclusive
    //console.log("showing lobby ");
    //console.trace();
    const root = document.getElementById('skeech-root');
    if (root) root.style.display = 'none';

    if (!document.getElementById('skeech-lobby')) {
      this.buildDOM();
    }
    this.lobbyRoot.style.display = 'block';

    // Double-check board is hidden after showing lobby
    if (root && root.style.display !== 'none') {
      root.style.display = 'none';
    }
    this.renderLobbyList(users);
    // Updates will come from presence sync (~every 15s via Supabase heartbeat)
  }

  /** Hide lobby and show the board grid */
  public hideLobby() {

    // Defensive guard: Ensure lobby and board are mutually exclusive
    this.lobbyRoot.style.display = 'none';

    // Double-check lobby is hidden before showing board
    if (this.lobbyRoot.style.display !== 'none') {
      this.lobbyRoot.style.display = 'none';
    }
    this.buildBoard();

    // Ensure board is visible after hiding lobby
    const root = document.getElementById('skeech-root');
    if (root) {
      root.style.display = 'grid';
    }
    this.showBoard();
  }








  async loadGamePreferences(hostUserId?: string) {
    const prefs = await this.profileManager.loadGamePreferences(hostUserId);
    if (prefs) {
      this.boardBackgroundColor = prefs.boardBackgroundColor;
      this.ballColor = prefs.ballColor;
    }
  }




  public showBoard() {
    // Defensive guard: Ensure lobby and board are mutually exclusive
    this.lobbyRoot.style.display = 'none';

    const el = document.getElementById('skeech-root');
    if (el) {
      el.style.display = 'grid';
    } else {
      // Retry after a tick if not ready
      setTimeout(this.showBoard, 50);
    }
  }

  /** Show dramatic retribution overlay */
  public showRetributionOverlay() {
    this.lightingFreezeUntil = performance.now() + 1000;
    const overlay = document.getElementById('retribution-overlay');
    if (overlay) {
      overlay.classList.add('visible');
    }
  }

  /** Hide retribution overlay */
  public hideRetributionOverlay() {
    
    const overlay = document.getElementById('retribution-overlay');
    if (overlay) {
      overlay.classList.remove('visible');
    }
  }

  /** Show sudden death skull overlay */
  public showSuddenDeathOverlay() {
    this.lightingFreezeUntil = performance.now() + 1000;
    const overlay = document.getElementById('sudden-death-overlay');
    if (overlay) {
      overlay.classList.add('visible');
    }
  }

  /** Hide sudden death overlay */
  public hideSuddenDeathOverlay() {

    const overlay = document.getElementById('sudden-death-overlay');
    if (overlay) {
      overlay.classList.remove('visible');
    }
  }

  /** Show observer mode badge */
  public showObserverBadge() {
    const badge = document.getElementById('observer-mode-badge');
    if (badge) {
      badge.classList.add('visible');
    }
  }

  /** Hide observer mode badge */
  public hideObserverBadge() {
    const badge = document.getElementById('observer-mode-badge');
    if (badge) {
      badge.classList.remove('visible');
    }
  }

  /** Re-render list (call whenever presence sync changes) */
  public updateLobby(users: PresenceMeta[]) {
    if (this.lobbyRoot.style.display !== 'block') return; // ignore if hidden
    this.renderLobbyList(users);
  }

  /** Build list UI from presence users */
  private renderLobbyList(users: PresenceMeta[]) {
    // preserve selection if possible
    const selectedId = this.lobbySelected?.userId ?? null;
    this.lobbySelected = null;

    this.lobbyListEl.innerHTML = '';
    if (!users.length) {
      const empty = document.createElement('div');
      empty.style.cssText = 'opacity:.75;padding:10px;font-size:13px';
      empty.textContent = 'No players online yet.';
      this.lobbyListEl.appendChild(empty);
      this.lobbyInviteBtn.disabled = true;
      return;
    }

    // Deduplicate users by userId (in case user has multiple tabs open)
    const uniqueUsers = new Map<string, PresenceMeta>();
    users.forEach(u => {
      if (!uniqueUsers.has(u.userId)) {
        uniqueUsers.set(u.userId, u);
      }
    });
    const deduplicatedUsers = Array.from(uniqueUsers.values());

    deduplicatedUsers.forEach(u => {
      const row = document.createElement('div');
      row.className = 'skeech-lobby-row';
      row.setAttribute('role', 'option');
      row.dataset.uid = u.userId;
      
      const isMe = u.userId === this.currentUser?.id;
      const inMatch = u.mode === 'in_match';
      const canInvite = !isMe && !inMatch;
      
      // Build the stats string
      if(!u.wins) u.wins = 0;
      if(!u.losses) u.losses = 0;
      if(!u.avgPPR) u.avgPPR = 0;
      
      const statsText = `W-L: ${u.wins}-${u.losses} | PPR: ${u.avgPPR.toFixed(2)}`;
      
      // Build status indicators
      let statusText = '';
      if (isMe) {
        statusText = '<span style="opacity:0.7; font-weight:normal;">(me)</span>';
      } else if (inMatch) {
        statusText = '<span style="opacity:0.6; font-weight:normal; color:#888;">(in a match)</span>';
      }
      
      // Build the row's inner HTML
      const displayName = `${u.userName} ${statusText}`;
      const avatarUrl = u.profile_image_url || avatarDataUriForName(u.userName);
      row.innerHTML = `
        <img src="${avatarUrl}" class="skeech-lobby-avatar" style="width: 40px; height: 40px; border-radius: 50%; object-fit: cover; margin-right: 12px; flex-shrink: 0;" />
        <div class="skeech-lobby-info">
          <div class="skeech-lobby-name">${displayName}</div>
          <div class="skeech-lobby-id">${statsText}</div>
        </div>
        <div class="skeech-lobby-row-actions" style="display: none;">
          <button class="skeech-lobby-action-btn" data-action="profile">🪪</button>
          <button class="skeech-lobby-action-btn" data-action="challenge">⚔️</button>
        </div>
      `;

      // Add event handlers for inline action buttons
      if (canInvite) {
        const actionButtons = row.querySelectorAll('.skeech-lobby-action-btn');
        actionButtons.forEach(btn => {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const action = (btn as HTMLElement).dataset.action;
            if (action === 'profile') {
              this.handlers.showUserProfile?.(u.userId, u.userName);
            } else if (action === 'challenge') {
              this.handlers.lobbyInvite?.(u);
            }
          });
        });
      }

      // Handle selection logic
      if (!canInvite) {
        row.style.cursor = 'default';
        row.style.opacity = isMe ? '1' : '0.6'; // Dim players in matches
        row.classList.add('disabled');
      } else {
        row.onclick = () => this.selectLobbyRow(row, u);
      }
      
      this.lobbyListEl.appendChild(row);
      if (selectedId && u.userId === selectedId) {
        this.selectLobbyRow(row, u, /*scroll*/ false);
      }
    });

    // if nothing auto-selected, hide all action buttons
    if (!this.lobbySelected) {
      Array.from(this.lobbyListEl.querySelectorAll('.skeech-lobby-row-actions'))
        .forEach(el => (el as HTMLElement).style.display = 'none');
    }
  }

  private selectLobbyRow(row: HTMLDivElement, user: PresenceMeta, scroll: boolean = true) {
    // clear other selections and hide all action buttons
    Array.from(this.lobbyListEl.querySelectorAll('.skeech-lobby-row.selected'))
      .forEach(el => {
        el.classList.remove('selected');
        const actions = el.querySelector('.skeech-lobby-row-actions') as HTMLElement;
        if (actions) actions.style.display = 'none';
      });

    row.classList.add('selected');
    if (scroll) row.scrollIntoView({ block: 'nearest' });

    this.lobbySelected = user;

    // Show inline actions for selected user
    const canInvite = user.userId !== this.currentUser?.id && user.mode !== 'in_match';
    if (canInvite) {
      const actions = row.querySelector('.skeech-lobby-row-actions') as HTMLElement;
      if (actions) actions.style.display = 'flex';
    }
  }


  public showIncomingInvite(fromUserName: string, onAccept: () => void, onDecline: () => void) {
    this.overlay.innerHTML = RendererTemplates.incomingInvite(fromUserName);
    this.overlay.style.display = 'flex';
    this.handlers.pauseTimer?.();

    this.overlay.querySelector<HTMLButtonElement>('#invAccept')!.onclick = () => {
      this.overlay.style.display = 'none';
      this.hideLobby();
      onAccept();
    };
    this.overlay.querySelector<HTMLButtonElement>('#invDecline')!.onclick = () => {
      this.overlay.style.display = 'none';
      this.handlers.resumeTimer?.();
      onDecline();
    };
  }

  public showRestartRequest(fromUserName: string, onAccept: () => void, onDecline: () => void) {
    this.showTwoButtonDialog(
      `${fromUserName} wants to restart the match.`,
      "❌",
      () => {
        this.hideOverlay();
        onDecline();
      },
      "⚔️",
      () => {
        this.hideOverlay();
        onAccept();
      }
    );
  }

  public showRestartAccepted(fromUserName: string) {
    this.showModal({
      title: "Restart Accepted",
      body: `${fromUserName} accepted your restart request. The game will now restart.`,
      button: "OK"
    });
  }

  public showRestartDeclined(fromUserName:string) {
    this.showModal({
      title: "Restart Declined",
      body: `${fromUserName} declined your restart request.`,
      button: "OK"
    });
  }

    public showInviteCancelled(fromUserName: string) {
      this.hideOverlay(); // Close the invite dialog
      this.showModal({
        title: "Invite Canceled",
        body: `Your match invite from <strong>${fromUserName}</strong> was canceled.`,
        button: "OK"
      });
    }

  public showMatchCancelled(fromUserName: string, onClose?: () => void) {
    this.hideOverlay(); // Close the match intro dialog
    this.stopPinballLightShow(); // Stop light show animation
    this.showModal({
      title: "Match Cancelled",
      body: `<strong>${fromUserName}</strong> cancelled the match.`,
      button: "OK",
      onClose: onClose
    });
  }

  public showInviteDeclined(fromUserName: string) {
    this.hideOverlay();
    this.showModal({
      title: "Invite Declined",
      body: `<strong>${fromUserName}</strong> declined your match invite, wtf`,
      button: "OK"
    });
  }

  public showQuitGameConfirmation(confirmQuit: () => void) {
    // Create a custom modal with two buttons for quit confirmation
    this.overlay.style.display = 'flex';
    this.overlay.innerHTML = RendererTemplates.quitConfirmation("");

    const cancelBtn = this.overlay.querySelector<HTMLButtonElement>('#cancelQuit')!;
    const confirmBtn = this.overlay.querySelector<HTMLButtonElement>('#confirmQuit')!;

    cancelBtn.onclick = () => {
      this.overlay.style.display = 'none';
    };

    confirmBtn.onclick = () => {
      this.overlay.style.display = 'none';
      confirmQuit();
    };
  }

  public showOpponentQuit(opponentName: string) {
    this.opponentHasQuit = true;
    this.showModal({
      title: "🏆 Victory!",
      body: RendererTemplates.forfeitNotification(opponentName),
      button: "ðŸ¥‡",
      onClose: () => {
        // Return to lobby
        this.handlers.returnToLobby?.();
      }
    });
  }

  // ** Match searching functionality **
  public showMatchSearching(cancelSearch: () => void) {
    this.overlay.innerHTML = RendererTemplates.matchSearching();
    this.overlay.style.display = 'flex';
    this.modalOpen = true;

    const cancelBtn = this.overlay.querySelector('#cancelSearchBtn') as HTMLButtonElement;
    if (cancelBtn) {
      cancelBtn.onclick = () => {
        this.hideOverlay();
        this.modalOpen = false;
        cancelSearch();
      };
    }
  }

  public hideSearchingDialog() {
    this.hideOverlay();
    this.modalOpen = false;
  }

  public showMatchIntroduction(currentPlayer: any, opponent: any, isStarting: boolean, onStartMatch: () => void, onCancel: () => void, titleText?: string, hideWL?: boolean) {
    // Start pinball light show for multiplayer matches
    this.startPinballLightShow();

    this.overlay.innerHTML = RendererTemplates.matchIntroduction(currentPlayer, opponent, isStarting, titleText, hideWL);
    this.overlay.style.display = 'flex';
    this.modalOpen = true;

    const startBtn = this.overlay.querySelector('#startMatchBtn') as HTMLButtonElement;
    const cancelBtn = this.overlay.querySelector('#cancelMatchBtn') as HTMLButtonElement;

    if (startBtn) {
      startBtn.onclick = () => {
        // Disable button and show spinner
        startBtn.disabled = true;
        startBtn.classList.add('waiting');

        const iconEl = startBtn.querySelector('.icon');
        const textEl = startBtn.querySelector('.text');

        if (iconEl) {
          iconEl.innerHTML = '<div class="search-spinner"></div>';
        }
        if (textEl) {
          (textEl as HTMLElement).textContent = 'Waiting...';
        }

        // Call onStartMatch to signal readiness
        onStartMatch();
      };
    }

    if (cancelBtn) {
      cancelBtn.onclick = () => {
        this.hideOverlay();
        this.modalOpen = false;
        this.stopPinballLightShow();
        // Call onCancel to notify opponent
        onCancel();
      };
    }
  }

  public showAIPromptInSearchModal() {
    if (!this.modalOpen) return; // Only act if the search modal is open

    const body = this.overlay.querySelector('.search-body') as HTMLDivElement;
    const actions = this.overlay.querySelector('#search-actions') as HTMLDivElement;

    if (body) {
      body.textContent = "It's a ghost town in here. Play the AI instead?";
    }

    if (actions && !actions.querySelector('#playAIFromSearch')) {
      const playAIButton = document.createElement('button');
      playAIButton.id = 'playAIFromSearch';
      playAIButton.className = 'skeech-btn-icon'; // Use primary button style
      playAIButton.textContent = '🤖';
      playAIButton.onclick = () => {
        this.hideOverlay();
        this.handlers.lobbySolo?.();
      };
      // Add the Play AI button before the Cancel button
      actions.prepend(playAIButton);
    }
  }

  public setTournamentMode(isTournament: boolean) {
    this.isTournamentGame = isTournament;
    // Re-render menu to reflect new options
    this.renderGameMenu();
  }

  public setTournamentPlayerRecords(records: Record<string, { wins: number; losses: number }>) {
    this.tournamentPlayerRecords = records;
  }

  // UI-only Tournament flow
  private randomJoinCode(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let out = '';
    for (let i = 0; i < 4; i++) out += chars[Math.floor(Math.random() * chars.length)];
    return out;
  }

  public async showTournamentEntry() {
    this.overlay.innerHTML = RendererTemplates.tournamentEntry();
    this.overlay.style.display = 'flex';
    this.modalOpen = true;

    // Close button handler
    const closeBtn = this.overlay.querySelector('#tournamentClose') as HTMLButtonElement | null;
    if (closeBtn) {
      closeBtn.onclick = () => {
        this.hideOverlay();
        this.modalOpen = false;
      };
    }

    const startBtn = this.overlay.querySelector('#tourneyStartBtn') as HTMLButtonElement | null;
    const joinInput = this.overlay.querySelector('#tourneyJoinCode') as HTMLInputElement | null;
    const joinSection = this.overlay.querySelector('#tourneyJoinSection') as HTMLDivElement | null;
    const errorEl = this.overlay.querySelector('#tourneyJoinError') as HTMLDivElement | null;
    const originalJoinHTML = joinSection ? joinSection.innerHTML : '';

    if (joinInput) {
      joinInput.placeholder = 'ex: ' + this.randomJoinCode();
      joinInput.value = '';
      let pendingTimer: number | null = null;
      let joining = false;
      const clearTimer = () => { if (pendingTimer !== null) { clearTimeout(pendingTimer); pendingTimer = null; } };
      const setError = (msg: string) => { if (errorEl) errorEl.textContent = msg; };
      const onInput = () => {
        // Sanitize and enforce A-Z0-9, length <= 6
        const raw = joinInput.value || '';
        const cleaned = raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
        if (cleaned !== joinInput.value) joinInput.value = cleaned;
        setError('');
        clearTimer();
        if (cleaned.length === 4 && !joining) {
          joining = true;
          (async () => {
            try {
              const result = await this.handlers.joinTournamentByCode?.(cleaned);
              // Success: go to lobby
              this.hideOverlay();
              this.modalOpen = false;
              if (result?.tournamentId) this.handlers.goToTournamentLobby?.(result.tournamentId);
            } catch (e: any) {
              // Show subtle error
              setError('tournament code not found');
            } finally {
              joining = false;
            }
          })();
        }
      };
      joinInput.addEventListener('input', onInput);
    }

    // Helper to format tournament status/placement for current user
    const formatTournamentStatus = (tournament: any): string => {
      const { status, bracket_data } = tournament;
      const me = this.currentUser?.id;
      const bd = bracket_data;

      if (bd?.rounds && me) {
        const rounds: any[] = bd.rounds || [];
        const totalRounds = rounds.length || 1;
        // Search from final back to round 1 for the user's last decided match
        for (let idx = totalRounds - 1; idx >= 0; idx--) {
          const rNum = rounds[idx].round;
          for (const m of rounds[idx].matches) {
            const inMatch = (m.a?.user_id === me) || (m.b?.user_id === me);
            if (!inMatch) continue;
            if (!m.result) {
              // No result yet
              return status === 'in_progress' ? `Round ${rNum}` : 'In Progress';
            }
            const aScore = m.result?.aScore ?? 0;
            const bScore = m.result?.bScore ?? 0;
            const aWon = aScore >= bScore;
            const meIsA = m.a?.user_id === me;
            const meWon = meIsA ? aWon : !aWon;
            if (meWon) {
              if (rNum === totalRounds) return '🥇 1st place';
              // Won this round but knocked out later; keep searching earlier rounds
              continue;
            } else {
              if (rNum === totalRounds) return '🥈 2nd place';
              return `Lost in Round ${rNum}`;
            }
          }
        }
        // If we never found a match for the user, fall back to status
      }

      // Fallbacks
      if (status === 'in_progress' && bd?.rounds) {
        const firstPending = bd.rounds.find((r: any) => r.matches.some((m: any) => !m.result));
        if (firstPending) return `Round ${firstPending.round}`;
        const lastRound = bd.rounds[bd.rounds.length - 1];
        return lastRound ? `Round ${lastRound.round}` : 'In Progress';
      }

      return status.split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    // Populate "Your Tournaments" list
    const myList = this.overlay.querySelector('#tourneyMyList') as HTMLDivElement | null;
    if (myList && this.handlers.listMyTournaments) {
      try {
        const rows = await this.handlers.listMyTournaments();
        if (!rows || rows.length === 0) {
          myList.innerHTML = '<div style="opacity:.7;">No tournaments yet</div>';
        } else {
          myList.innerHTML = rows.map(r => {
            const isHost = r.creator_id && this.currentUser && r.creator_id === this.currentUser.id;
            const name = `${r.name}${isHost ? ' (host)' : ''}`;
            return `
              <div class="tournament-my-item" data-id="${r.id}">
                <div class="tournament-my-name">${name}</div>
                <div class="tournament-my-status">${formatTournamentStatus(r)}</div>
              </div>
            `;
          }).join('');
          myList.querySelectorAll('.tournament-my-item').forEach(el => {
            el.addEventListener('click', () => {
              const id = (el as HTMLElement).getAttribute('data-id')!;
              this.hideOverlay();
              this.modalOpen = false;
              this.handlers.goToTournamentLobby?.(id);
            });
          });
        }
      } catch {}
    }

    if (startBtn && joinSection) {
      startBtn.onclick = () => {
        // Replace join section with creation form (name + submit + cancel)
        joinSection.innerHTML = `
          <label for="tourneyName" class="tournament-label">Tournament Name</label>
          <input id="tourneyName" type="text" class="tournament-input" placeholder="2025 SkrollrDerby" />
          <div class="tournament-type-row" style="display:flex; align-items:center; justify-content:center; gap:12px; margin: 8px 0 10px 0;">
            <span id="tTypeLabelLeft" style="font-size:14px; color:#666;">Bracket</span>
            <div id="tourneyTypeToggle" class="lobby-toggle-container">
              <div id="tTypeBracket" class="lobby-toggle selected"></div>
              <div id="tTypeRR" class="lobby-toggle"></div>
            </div>
            <span id="tTypeLabelRight" style="font-size:14px; color:#666;">Round-Robin</span>
          </div>
          <div id="tourneyTypeInfo" class="tournament-type-info" style="margin-bottom: 12px;">
            <strong>Bracket:</strong> Fast, players eliminated after one loss. Best for larger groups.
          </div>
          <div class="tournament-create-actions">
            <button id="tourneyCreateCancel" class="skeech-btn-icon tournament-cancel-btn" style="flex:1;">
              <div class="icon">❌</div>
              <div class="text">Cancel</div>
            </button>
            <button id="tourneyCreateSubmit" class="skeech-btn-icon" style="flex:1;">
              <div class="icon">🏆</div>
              <div class="text">Create</div>
            </button>
          </div>
        `

        let selectedType: 'bracket' | 'round_robin' = 'bracket';
        const typeInfo = joinSection.querySelector('#tourneyTypeInfo') as HTMLDivElement | null;
        const updateTypeInfo = (value: 'bracket' | 'round_robin') => {
          if (!typeInfo) return;
          if (value === 'round_robin') {
            typeInfo.innerHTML = '<strong>Round-Robin:</strong> Everyone plays everyone once. Best for 4-8 players.';
          } else {
            typeInfo.innerHTML = '<strong>Bracket:</strong> Fast, players eliminated after one loss. Best for larger groups.';
          }
        };
        const tBtnBracket = joinSection.querySelector('#tTypeBracket') as HTMLDivElement | null;
        const tBtnRR = joinSection.querySelector('#tTypeRR') as HTMLDivElement | null;
        const applyToggleUI = () => {
          if (selectedType === 'bracket') {
            tBtnBracket?.classList.add('selected');
            tBtnRR?.classList.remove('selected');
          } else {
            tBtnRR?.classList.add('selected');
            tBtnBracket?.classList.remove('selected');
          }
          updateTypeInfo(selectedType);
        };
        tBtnBracket?.addEventListener('click', () => { selectedType = 'bracket'; applyToggleUI(); });
        tBtnRR?.addEventListener('click', () => { selectedType = 'round_robin'; applyToggleUI(); });
        applyToggleUI();
        const submit = joinSection.querySelector('#tourneyCreateSubmit') as HTMLButtonElement | null;
        const cancel = joinSection.querySelector('#tourneyCreateCancel') as HTMLButtonElement | null;
        // Ensure order: Cancel (left), Create (right)
        const parent = joinSection.querySelector('.tournament-create-actions') as HTMLDivElement | null;
        if (parent && submit && cancel) {
          parent.innerHTML = '';
          parent.appendChild(cancel);
          parent.appendChild(submit);
        }
        if (submit) {
          submit.onclick = async () => {
            const nameInput = joinSection.querySelector('#tourneyName') as HTMLInputElement | null;
            const name = (nameInput?.value || '').trim() || "My Tournament";
            const type = selectedType;
            const joinCode = this.randomJoinCode();
            try {
              const created = await this.handlers.createTournament?.({ name, joinCode, maxPlayers: 16, type });
              const code = created?.join_code || joinCode;
              // Show the code prominently with copy button
              this.overlay.innerHTML = RendererTemplates.tournamentCreated(name, code);
              const copyBtn = this.overlay.querySelector('#copyTourneyCode') as HTMLButtonElement | null;
              const toast = this.overlay.querySelector('#copyToast') as HTMLDivElement | null;
              const goBtn = this.overlay.querySelector('#goTourneyLobby') as HTMLButtonElement | null;

              const doToast = (msg: string) => {
                if (!toast) return;
                toast.textContent = msg;
                toast.classList.add('show');
                setTimeout(() => toast.classList.remove('show'), 1200);
              };

              const robustCopy = async (text: string) => {
                try {
                  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
                    await navigator.clipboard.writeText(text);
                    return true;
                  }
                } catch {}
                try {
                  const ta = document.createElement('textarea');
                  ta.value = text;
                  ta.style.position = 'fixed';
                  ta.style.left = '-9999px';
                  document.body.appendChild(ta);
                  ta.focus();
                  ta.select();
                  const ok = document.execCommand('copy');
                  document.body.removeChild(ta);
                  return ok;
                } catch {
                  return false;
                }
              };

              if (copyBtn) {
                copyBtn.onclick = async () => {
                  const ok = await robustCopy(code);
                  doToast(ok ? 'Copied!' : 'Copy failed');
                };
              }
              if (goBtn) {
                goBtn.onclick = () => {
                  const id = (created as any)?.id;
                  this.hideOverlay();
                  this.modalOpen = false;
                  this.handlers.goToTournamentLobby?.(id);
                };
              }
            } catch (e) {
              // On error, show a soft message in place
              const err = document.createElement('div');
              err.style.color = '#ff9b9b';
              err.style.fontSize = '12px';
              err.style.marginTop = '8px';
              err.textContent = 'Failed to create tournament. Please try again.';
              joinSection.appendChild(err);
            }
          };
        }
        if (cancel) {
          cancel.onclick = () => {
            // Exit the tournament modal entirely
            this.hideOverlay();
            this.modalOpen = false;
          };
        }
      };
    }
  }

}









